import React, { lazy, Suspense } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { CssBaseline } from "@mui/material";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { SnackbarProvider } from "notistack";

// Contexts
import { AuthProvider } from "@contexts/AuthContext";
import { ThemeProvider } from "@contexts/ThemeContext";
import { EmpresaProvider } from "@contexts/EmpresaContext";

// Components
import Layout from "@components/layout/Layout";
import ProtectedRoute from "@components/common/ProtectedRoute";
import InicioPorPapel from "@components/common/InicioPorPapel";
import LoadingScreen from "@components/common/LoadingScreen";
import ErroDeCarregamento from "@components/common/ErroDeCarregamento";

// Pages: cada uma vira um arquivo (chunk) baixado só quando a rota é aberta. O
// bundle inicial fica com o que toda tela precisa (providers, layout, rotas) e
// as bibliotecas pesadas (gráficos, formulários) vêm junto da página que as usa.
const Login = lazy(() => import("@pages/Login"));
const Dashboard = lazy(() => import("@pages/Dashboard"));
const Clientes = lazy(() => import("@pages/Clientes"));
const Produtos = lazy(() => import("@pages/Produtos"));
const GruposProduto = lazy(() => import("@pages/GruposProduto"));
const Pedidos = lazy(() => import("@pages/Pedidos"));
const Orcamentos = lazy(() => import("@pages/Orcamentos"));
const Estoque = lazy(() => import("@pages/Estoque"));
const Relatorios = lazy(() => import("@pages/Relatorios"));
const Configuracoes = lazy(() => import("@pages/Configuracoes"));
const UsuariosConfiguracoes = lazy(
  () => import("@pages/configuracoes/Usuarios"),
);
const Empresas = lazy(() => import("@pages/Empresas"));
const EmpresaDetalhe = lazy(() => import("@pages/EmpresaDetalhe"));
const Perfil = lazy(() => import("@pages/Perfil"));
const NotFound = lazy(() => import("@pages/NotFound"));

// Create a client for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <CssBaseline />
        <SnackbarProvider
          maxSnack={3}
          anchorOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          autoHideDuration={5000}
        >
          <AuthProvider>
            <EmpresaProvider>
              <Router>
                <ErroDeCarregamento>
                  <Suspense fallback={<LoadingScreen />}>
                    <Routes>
                      {/* Public Routes */}
                      <Route path="/login" element={<Login />} />

                      {/* Protected Routes */}
                      <Route
                        path="/"
                        element={
                          <ProtectedRoute>
                            <Layout />
                          </ProtectedRoute>
                        }
                      >
                        <Route index element={<InicioPorPapel />} />
                        <Route
                          path="dashboard"
                          element={
                            <ProtectedRoute papeis={["administrador"]}>
                              <Dashboard />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="clientes"
                          element={
                            <ProtectedRoute papeis={["administrador"]}>
                              <Clientes />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="produtos"
                          element={
                            <ProtectedRoute
                              papeis={["administrador", "operador_estoque"]}
                            >
                              <Produtos />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="grupos-produto"
                          element={
                            <ProtectedRoute
                              papeis={["administrador", "operador_estoque"]}
                            >
                              <GruposProduto />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="pedidos"
                          element={
                            <ProtectedRoute
                              papeis={["administrador", "operador_entrega"]}
                            >
                              <Pedidos />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="orcamentos"
                          element={
                            <ProtectedRoute papeis={["administrador"]}>
                              <Orcamentos />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="estoque"
                          element={
                            <ProtectedRoute
                              papeis={["administrador", "operador_estoque"]}
                            >
                              <Estoque />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="relatorios"
                          element={
                            <ProtectedRoute papeis={["administrador"]}>
                              <Relatorios />
                            </ProtectedRoute>
                          }
                        />
                        <Route path="perfil" element={<Perfil />} />

                        {/* Gestão de Empresas (qualquer usuário autenticado) */}
                        <Route path="empresa" element={<Empresas />} />
                        <Route
                          path="empresa/:id"
                          element={<EmpresaDetalhe />}
                        />

                        {/* Admin Only Routes */}
                        <Route
                          path="configuracoes/*"
                          element={
                            <ProtectedRoute adminOnly>
                              <Configuracoes />
                            </ProtectedRoute>
                          }
                        >
                          <Route
                            index
                            element={<Navigate to="usuarios" replace />}
                          />
                          <Route
                            path="usuarios"
                            element={<UsuariosConfiguracoes />}
                          />
                        </Route>
                      </Route>

                      {/* 404 Route */}
                      <Route path="*" element={<NotFound />} />
                    </Routes>
                  </Suspense>
                </ErroDeCarregamento>
              </Router>
            </EmpresaProvider>
          </AuthProvider>
        </SnackbarProvider>

        {/* React Query DevTools (only in development) */}
        {import.meta.env.DEV && <ReactQueryDevtools />}
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
